import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  alpha,
  Slide,
  Backdrop,
  useTheme,
  ThemeProvider,
  createTheme,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Gavel as GavelIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  AccessibilityNew as AccessibilityNewIcon,
  Person as PersonIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';

import { useTheme as useCustomTheme } from '../context/ThemeContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../services/api';


// Importando a fonte Montserrat via CSS
import '@fontsource/montserrat/300.css';
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/700.css';

// Transição personalizada para o Dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Transição personalizada para o Dialog de senha emitida
const SenhaTransition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const EmitirSenha = () => {
  const baseTheme = useTheme();
  const { isDarkMode, toggleTheme } = useCustomTheme();
  const [servicos, setServicos] = useState([]);
  const [dialogoAberto, setDialogoAberto] = useState(false);
  const [servicoSelecionado, setServicoSelecionado] = useState(null);
  const [senhaEmitida, setSenhaEmitida] = useState(null);
  const [dialogoSenhaAberto, setDialogoSenhaAberto] = useState(false);

  // Adicionar useEffect para fechar o diálogo automaticamente
  useEffect(() => {
    let timeoutId;
    if (dialogoSenhaAberto) {
      timeoutId = setTimeout(() => {
        setDialogoSenhaAberto(false);
      }, 4000); // 4 segundos
    }
    // Cleanup do timeout se o componente for desmontado ou o diálogo for fechado manualmente
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [dialogoSenhaAberto]);

  // Buscar serviços do backend
  useEffect(() => {
    const fetchServicos = async () => {
      try {
        const response = await api.get('/servicos');
        if (response.data.success) {
          // Filtrar apenas serviços habilitados para emissão e mapear para incluir ícones
          const servicosHabilitados = response.data.data.filter(servico => 
            servico.ativo && servico.exibir_na_emissao
          );
          
          const servicosComIcones = servicosHabilitados.map(servico => ({
            id: servico.id,
            nome: servico.nome,
            abreviacao: servico.abreviacao,
            icon: getServicoIcon(servico.abreviacao),
            cor: getServicoCor(servico.abreviacao),
          }));
          setServicos(servicosComIcones);
        }
      } catch (error) {
        console.error('Erro ao buscar serviços:', error);
        toast.error('Erro ao carregar serviços disponíveis');
      }
    };

    fetchServicos();
  }, []);

  // Função para obter o ícone baseado na abreviação do serviço
  const getServicoIcon = (abreviacao) => {
    const icones = {
      'SV': DescriptionIcon,
      'AP': AssignmentIcon,
      'AE': DescriptionIcon,
      'AT': GavelIcon,
      'ME': PersonIcon,
      'PA': AssignmentIcon,
      'PV': AssignmentIcon,
      'PP': AssignmentIcon,
      'PO': GavelIcon,
      'PE': DescriptionIcon,
      'RA': DescriptionIcon,
    };
    return icones[abreviacao] || DescriptionIcon;
  };

  // Função para obter a cor baseada na abreviação do serviço
  const getServicoCor = (abreviacao) => {
    const cores = {
      'SV': '#4CAF50',
      'AP': '#2196F3',
      'AE': '#9C27B0',
      'AT': '#FF9800',
      'ME': '#F44336',
      'PA': '#3F51B5',
      'PV': '#009688',
      'PP': '#673AB7',
      'PO': '#795548',
      'PE': '#607D8B',
      'RA': '#E91E63',
    };
    return cores[abreviacao] || '#4CAF50';
  };

  // Criando um tema personalizado com a fonte Montserrat
  const theme = createTheme({
    ...baseTheme,
    typography: {
      fontFamily: [
        'Montserrat',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(','),
      h1: {
        fontFamily: 'Montserrat',
        fontWeight: 700,
      },
      h2: {
        fontFamily: 'Montserrat',
        fontWeight: 700,
      },
      h3: {
        fontFamily: 'Montserrat',
        fontWeight: 600,
      },
      h4: {
        fontFamily: 'Montserrat',
        fontWeight: 600,
      },
      h5: {
        fontFamily: 'Montserrat',
        fontWeight: 500,
      },
      h6: {
        fontFamily: 'Montserrat',
        fontWeight: 500,
      },
      button: {
        fontFamily: 'Montserrat',
        fontWeight: 500,
      },
    },
  });

  // Manipulador para clicar em um serviço
  const handleClicarServico = (servico) => {
    setServicoSelecionado(servico);
    setDialogoAberto(true);
  };

  // Manipulador para fechar o diálogo
  const handleFecharDialogo = () => {
    setDialogoAberto(false);
    setServicoSelecionado(null);
  };



  const handleEmitirSenha = async (isPreferencial) => {
    if (!servicoSelecionado) {
      toast.error('Por favor, selecione um serviço');
      return;
    }

    try {
      console.log('4. handleEmitirSenha - Valores antes do envio:', {
        isPreferencial,
        tipo: typeof isPreferencial,
        servicoSelecionado: servicoSelecionado.nome
      });

      const dadosParaEnviar = {
        servico_nome: servicoSelecionado.nome,
        preferencial: isPreferencial
      };
      console.log('5. Dados que serão enviados para a API:', dadosParaEnviar);

      const response = await api.post('/senhas/emitir', dadosParaEnviar);
      console.log('6. Resposta recebida da API:', response.data);
      
      if (response.data.success) {
        const senhaData = response.data.data;
        console.log('7. Dados processados antes de atualizar o estado:', {
          senhaData,
          preferencial_original: senhaData.preferencial
        });

        setSenhaEmitida(senhaData);
        setDialogoAberto(false);
        setDialogoSenhaAberto(true);
        toast.success('Senha emitida com sucesso!');
        
        // Verificar o resultado da impressão
        if (response.data.impressao) {
          if (response.data.impressao.sucesso) {
            toast.success('Senha enviada para impressora');
          } else {
            toast.warning('Não foi possível imprimir a senha. Verifique a impressora.');
          }
        }
      } else {
        console.log('8. Erro na resposta da API:', response.data);
        toast.error('Erro ao emitir senha: ' + (response.data.message || 'Erro desconhecido'));
      }
    } catch (error) {
      console.error('9. Erro ao emitir senha:', error);
      toast.error('Erro ao emitir senha: ' + (error.response?.data?.message || 'Erro desconhecido'));
    } finally {
      console.log('10. Finalizando - Resetando estados');
    }
  };



  return (
    <ThemeProvider theme={theme}>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDarkMode ? "dark" : "light"}
      />
      <Box sx={{ 
        display: 'flex', 
        height: '100vh', 
        bgcolor: isDarkMode ? 'background.default' : alpha('#000', 0.03),
        flexDirection: 'column',
        transition: 'background-color 0.3s ease'
      }}>
        {/* Header simplificado apenas com toggle de tema */}
        <AppBar 
          position="fixed" 
          elevation={0}
          sx={{
            bgcolor: theme => theme.palette.mode === 'dark' 
              ? alpha(theme.palette.background.paper, 0.8)
              : alpha(theme.palette.background.paper, 0.9),
            backdropFilter: 'blur(8px)',
            borderBottom: '1px solid',
            borderColor: theme => theme.palette.mode === 'dark'
              ? alpha(theme.palette.primary.main, 0.1)
              : alpha(theme.palette.divider, 0.1),
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                fontWeight: 600,
                background: theme => theme.palette.mode === 'dark'
                  ? 'linear-gradient(45deg, #90caf9, #42a5f5)'
                  : 'linear-gradient(45deg, #1976d2, #42a5f5)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: theme => theme.palette.mode === 'dark' 
                  ? '0 0 30px rgba(144, 202, 249, 0.2)' 
                  : 'none',
              }}
            >
              Emissão de Senhas
            </Typography>

            {/* Toggle de tema */}
            <IconButton
              onClick={toggleTheme}
              sx={{
                p: 1,
                border: '1px solid',
                borderColor: theme => theme.palette.mode === 'dark'
                  ? alpha(theme.palette.primary.main, 0.2)
                  : alpha(theme.palette.grey[300], 0.5),
                borderRadius: 2,
                bgcolor: theme => theme.palette.mode === 'dark'
                  ? alpha(theme.palette.primary.main, 0.1)
                  : alpha(theme.palette.grey[50], 0.8),
                '&:hover': {
                  bgcolor: theme => theme.palette.mode === 'dark'
                    ? alpha(theme.palette.primary.main, 0.2)
                    : alpha(theme.palette.grey[100], 0.8),
                },
                transition: 'all 0.3s ease',
              }}
            >
              {isDarkMode ? (
                <LightModeIcon 
                  sx={{ 
                    color: 'primary.light',
                    filter: 'drop-shadow(0 0 8px rgba(144, 202, 249, 0.4))'
                  }} 
                />
              ) : (
                <DarkModeIcon 
                  sx={{ 
                    color: 'primary.main'
                  }} 
                />
              )}
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Conteúdo principal */}
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3, mt: 8 }}>
          <Container maxWidth="100%">
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                mb: 4,
                fontWeight: 600,
                fontFamily: 'Montserrat',
                background: isDarkMode 
                  ? 'linear-gradient(45deg, #90caf9, #42a5f5)' 
                  : 'linear-gradient(45deg, #1976d2, #42a5f5)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: isDarkMode ? '0 0 30px rgba(144, 202, 249, 0.2)' : 'none',
              }}
            >
              Selecione o Serviço
            </Typography>

            {servicos.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 8,
                  px: 4,
                }}
              >
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.grey[500], 0.1),
                    mb: 3,
                  }}
                >
                  <SettingsIcon sx={{ 
                    fontSize: 60, 
                    color: theme.palette.grey[500],
                  }} />
                </Box>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.secondary',
                    fontFamily: 'Montserrat',
                    mb: 2,
                    textAlign: 'center',
                  }}
                >
                  Nenhum Serviço Disponível
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: 'text.secondary',
                    textAlign: 'center',
                    maxWidth: 400,
                    lineHeight: 1.6,
                  }}
                >
                  Não há serviços habilitados para emissão de senhas no momento. 
                  Entre em contato com o administrador para habilitar os serviços necessários.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {servicos.map((servico) => (
                  <Grid item xs={12} sm={6} md={4} key={servico.id}>
                    <Paper
                      elevation={0}
                      onClick={() => handleClicarServico(servico)}
                      sx={{
                        p: 3,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        borderRadius: 4,
                        backgroundColor: isDarkMode ? alpha('#000', 0.4) : 'background.paper',
                        border: `1px solid ${alpha(servico.cor, isDarkMode ? 0.3 : 0.2)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 12px 24px ${alpha(servico.cor, isDarkMode ? 0.4 : 0.2)}`,
                          bgcolor: alpha(servico.cor, isDarkMode ? 0.15 : 0.05),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          bgcolor: alpha(servico.cor, isDarkMode ? 0.2 : 0.1),
                          mb: 2,
                          transition: 'all 0.3s ease',
                          boxShadow: isDarkMode 
                            ? `0 0 20px ${alpha(servico.cor, 0.3)}`
                            : 'none',
                        }}
                      >
                        <servico.icon sx={{ 
                          fontSize: 40, 
                          color: servico.cor,
                          filter: isDarkMode ? 'brightness(1.2)' : 'none',
                        }} />
                      </Box>
                      <Typography 
                        variant="h5" 
                        component="h2" 
                        sx={{ 
                          fontWeight: 600, 
                          color: servico.cor,
                          fontFamily: 'Montserrat',
                          textShadow: isDarkMode 
                            ? `0 0 10px ${alpha(servico.cor, 0.4)}`
                            : 'none',
                        }}
                      >
                        {servico.nome}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Container>
        </Box>

        {/* Diálogo de seleção de tipo de senha */}
        <Dialog
          open={dialogoAberto}
          onClose={handleFecharDialogo}
          TransitionComponent={Transition}
          maxWidth="xs"
          fullWidth
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
            sx: {
              backgroundColor: alpha('#000', isDarkMode ? 0.7 : 0.5),
              backdropFilter: 'blur(4px)',
            },
          }}
          PaperProps={{
            sx: {
              borderRadius: 3,
              backgroundColor: isDarkMode ? alpha('#1a1a1a', 0.95) : 'background.paper',
              boxShadow: isDarkMode 
                ? '0 8px 32px rgba(0,0,0,0.4)'
                : '0 8px 32px rgba(0,0,0,0.2)',
              border: isDarkMode 
                ? `1px solid ${alpha('#fff', 0.1)}`
                : 'none',
            },
          }}
        >
          <DialogTitle sx={{ 
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <Typography variant="h6" component="div" sx={{ 
              fontWeight: 600, 
              fontFamily: 'Montserrat',
              color: isDarkMode ? 'primary.light' : 'text.primary',
            }}>
              Selecione o Tipo de Senha
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleFecharDialogo}
              aria-label="close"
              sx={{
                color: isDarkMode ? 'grey.400' : 'grey.500',
                '&:hover': {
                  color: isDarkMode ? 'grey.200' : 'grey.700',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    handleEmitirSenha(false);
                  }}
                  startIcon={<PersonIcon />}
                  sx={{
                    py: 3,
                    borderRadius: 3,
                    borderWidth: 2,
                    fontFamily: 'Montserrat',
                    borderColor: isDarkMode ? 'primary.light' : 'primary.main',
                    color: isDarkMode ? 'primary.light' : 'primary.main',
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: isDarkMode ? 'primary.light' : 'primary.main',
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  Senha Normal
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={() => {
                    handleEmitirSenha(true);
                  }}
                  startIcon={<AccessibilityNewIcon />}
                  sx={{
                    py: 3,
                    borderRadius: 3,
                    fontFamily: 'Montserrat',
                    background: isDarkMode
                      ? 'linear-gradient(45deg, #90caf9, #42a5f5)'
                      : 'linear-gradient(45deg, #1976d2, #42a5f5)',
                    boxShadow: isDarkMode
                      ? '0 4px 20px rgba(144, 202, 249, 0.4)'
                      : '0 4px 12px rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      background: isDarkMode
                        ? 'linear-gradient(45deg, #82b1ff, #2979ff)'
                        : 'linear-gradient(45deg, #1565c0, #1976d2)',
                      boxShadow: isDarkMode
                        ? '0 6px 24px rgba(144, 202, 249, 0.5)'
                        : '0 6px 16px rgba(25, 118, 210, 0.4)',
                    },
                  }}
                >
                  Senha Preferencial
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
        </Dialog>

        {/* Diálogo de senha emitida */}
        <Dialog
          open={dialogoSenhaAberto}
          onClose={() => setDialogoSenhaAberto(false)}
          TransitionComponent={SenhaTransition}
          maxWidth="sm"
          fullWidth
          BackdropProps={{
            sx: {
              backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(0, 0, 0, 0.8)',
              backdropFilter: 'blur(6px)',
              transition: 'all 0.3s ease-in-out',
            },
          }}
          PaperProps={{
            elevation: 0,
            sx: {
              borderRadius: 4,
              backgroundColor: isDarkMode ? alpha('#1a1a1a', 0.95) : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: isDarkMode 
                ? '0 8px 32px rgba(144, 202, 249, 0.2)'
                : '0 8px 32px rgba(0, 0, 0, 0.2)',
              border: isDarkMode 
                ? `1px solid ${alpha('#fff', 0.1)}`
                : 'none',
              overflow: 'hidden',
              transition: 'all 0.3s ease-in-out',
            },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              p: 3,
              background: isDarkMode
                ? `linear-gradient(135deg, ${alpha('#90caf9', 0.1)}, ${alpha('#42a5f5', 0.1)})`
                : `linear-gradient(135deg, ${alpha('#1976d2', 0.05)}, ${alpha('#42a5f5', 0.05)})`,
            }}
          >
            <IconButton
              aria-label="close"
              onClick={() => setDialogoSenhaAberto(false)}
              sx={{
                position: 'absolute',
                right: 16,
                top: 16,
                color: isDarkMode ? 'grey.400' : 'grey.500',
                bgcolor: isDarkMode ? alpha('#fff', 0.05) : alpha('#000', 0.05),
                '&:hover': {
                  bgcolor: isDarkMode ? alpha('#fff', 0.1) : alpha('#000', 0.1),
                  color: isDarkMode ? 'grey.200' : 'grey.700',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <CloseIcon />
            </IconButton>

            <Typography
              variant="h4"
              component="div"
              sx={{
                textAlign: 'center',
                fontWeight: 700,
                mb: 4,
                background: isDarkMode
                  ? 'linear-gradient(45deg, #90caf9, #42a5f5)'
                  : 'linear-gradient(45deg, #1976d2, #42a5f5)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: isDarkMode ? '0 0 30px rgba(144, 202, 249, 0.3)' : 'none',
                animation: 'fadeIn 0.5s ease-in-out',
              }}
            >
              Senha Emitida
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 4,
                px: 2,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '80%',
                  height: '1px',
                  background: isDarkMode
                    ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)'
                    : 'linear-gradient(90deg, transparent, rgba(0,0,0,0.1), transparent)',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '80%',
                  height: '1px',
                  background: isDarkMode
                    ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)'
                    : 'linear-gradient(90deg, transparent, rgba(0,0,0,0.1), transparent)',
                },
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: '4rem',
                  mb: 2,
                  color: isDarkMode ? 'primary.light' : 'text.primary',
                  textShadow: isDarkMode 
                    ? '0 0 30px rgba(144, 202, 249, 0.3)'
                    : '0 2px 4px rgba(0,0,0,0.1)',
                  animation: 'scaleIn 0.5s ease-in-out',
                }}
              >
                {senhaEmitida?.senha || ''}
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  color: isDarkMode ? 'grey.300' : 'text.secondary',
                  mb: 2,
                  fontWeight: 500,
                  textAlign: 'center',
                  animation: 'slideUp 0.5s ease-in-out',
                }}
              >
                {senhaEmitida?.servico_nome || ''}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: senhaEmitida?.preferencial 
                    ? isDarkMode ? 'primary.dark' : 'primary.main'
                    : isDarkMode ? alpha('#fff', 0.05) : 'grey.100',
                  color: senhaEmitida?.preferencial 
                    ? 'white'
                    : isDarkMode ? 'grey.300' : 'text.primary',
                  py: 1,
                  px: 3,
                  borderRadius: 2,
                  animation: 'fadeIn 0.5s ease-in-out',
                  transition: 'all 0.3s ease-in-out',
                  boxShadow: isDarkMode && senhaEmitida?.preferencial
                    ? '0 0 20px rgba(144, 202, 249, 0.3)'
                    : 'none',
                }}
              >
                {senhaEmitida?.preferencial ? (
                  <AccessibilityNewIcon fontSize="small" />
                ) : (
                  <PersonIcon fontSize="small" />
                )}
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 500,
                  }}
                >
                  {senhaEmitida?.preferencial ? 'Preferencial' : 'Normal'}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 4,
              }}
            >
              <Button
                variant="contained"
                onClick={() => setDialogoSenhaAberto(false)}
                sx={{
                  px: 6,
                  py: 1.5,
                  borderRadius: 3,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  background: isDarkMode
                    ? 'linear-gradient(45deg, #90caf9, #42a5f5)'
                    : 'linear-gradient(45deg, #1976d2, #42a5f5)',
                  boxShadow: isDarkMode
                    ? '0 4px 20px rgba(144, 202, 249, 0.4)'
                    : '0 4px 12px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    background: isDarkMode
                      ? 'linear-gradient(45deg, #82b1ff, #2979ff)'
                      : 'linear-gradient(45deg, #1565c0, #1976d2)',
                    boxShadow: isDarkMode
                      ? '0 6px 24px rgba(144, 202, 249, 0.5)'
                      : '0 6px 16px rgba(25, 118, 210, 0.4)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                Fechar
              </Button>
            </Box>
          </Box>

          <style>
            {`
              @keyframes fadeIn {
                from {
                  opacity: 0;
                }
                to {
                  opacity: 1;
                }
              }

              @keyframes scaleIn {
                from {
                  transform: scale(0.8);
                  opacity: 0;
                }
                to {
                  transform: scale(1);
                  opacity: 1;
                }
              }

              @keyframes slideUp {
                from {
                  transform: translateY(20px);
                  opacity: 0;
                }
                to {
                  transform: translateY(0);
                  opacity: 1;
                }
              }
            `}
          </style>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default EmitirSenha; 